"use client";
import type { ReactNode } from 'react';
import { Editor } from '@tiptap/react';
import { Globe, Twitter } from 'lucide-react';
import Image from 'next/image';

interface SEOSettingsProps {
  formData: {
    metaTitle: string;
    metaDescription: string;
    focusKeywords: string;
    canonicalUrl: string;
    ogTitle: string;
    ogDescription: string;
    ogImage: string;
    twitterTitle: string;
    twitterDescription: string;
    twitterImage: string;
    schemaMarkup: string;
    enableSchema: boolean;
    blogType?: string;
  };
  keywordDensity: {
    keyword: string;
    count: number;
    density: number;
    status: 'optimal' | 'too-low' | 'too-high';
  }[];
  editor: Editor | null;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  handleOgImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleTwitterImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  generateSchemaMarkup: () => void;
  calculateKeywordDensity: () => void;
  generateCanonicalUrl: () => void;
  /** Rendered below Open Graph + Twitter, still under “Social Media”. */
  afterSocialMediaBlock?: ReactNode;
}

export default function SEOSettings({
  formData,
  keywordDensity,
  editor,
  handleInputChange,
  handleOgImageUpload,
  handleTwitterImageUpload,
  generateSchemaMarkup,
  calculateKeywordDensity,
  generateCanonicalUrl,
  afterSocialMediaBlock,
}: SEOSettingsProps) {
  return (
    <div className="space-y-8" data-seo-section>
      {/* SEO Settings - Smooth Single Section */}
      <div className="space-y-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 tracking-tight mb-2">
            SEO Settings {formData.blogType === 'Thought & Leadership' ? <span className="text-sky-600">*</span> : <span className="text-sky-600">*</span>}
          </h3>
          <p className="text-sm text-gray-600 font-medium">
            ({formData.blogType === 'Thought & Leadership' ? 'Basic fields required' : 'All fields required'} for publishing)
          </p>
        </div>

        <div className="space-y-6">
          {/* Meta Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2 tracking-tight">
              Meta Title <span className="text-sky-600">*</span>
              <span className={`ml-2 text-xs font-medium ${formData.metaTitle.length > 60 ? 'text-red-600' : 'text-sky-600'}`}>
                ({formData.metaTitle.length}/60)
              </span>
            </label>
            <input
              type="text"
              name="metaTitle"
              value={formData.metaTitle}
              onChange={handleInputChange}
              className={`w-full border-2 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none ${
                formData.metaTitle.length > 60 
                  ? 'border-red-300 bg-red-50/50' 
                  : 'border-sky-200 bg-white hover:border-sky-300'
              }`}
              placeholder="SEO title for search engines"
            />
            {formData.metaTitle.length > 60 && (
              <p className="mt-1.5 text-sm font-medium text-red-600 flex items-center gap-1">
                <span>⚠️</span>
                Meta title exceeds recommended length. Search engines may truncate it.
              </p>
            )}
          </div>

          {/* Meta Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2 tracking-tight">
              Meta Description <span className="text-sky-600">*</span>
              <span className={`ml-2 text-xs font-medium ${formData.metaDescription.length > 160 ? 'text-amber-600' : 'text-sky-600'}`}>
                ({formData.metaDescription.length}/160)
              </span>
            </label>
            <textarea
              name="metaDescription"
              value={formData.metaDescription}
              onChange={handleInputChange}
              rows={3}
              className={`w-full border-2 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none resize-none ${
                formData.metaDescription.length > 160 
                  ? 'border-amber-300 bg-amber-50/50' 
                  : 'border-sky-200 bg-white hover:border-sky-300'
              }`}
              placeholder="Brief description for search results"
            />
            {formData.metaDescription.length > 160 && (
              <p className="mt-1.5 text-sm font-medium text-amber-600 flex items-center gap-1">
                <span>⚠️</span>
                Meta description exceeds recommended length. Search engines may truncate it.
              </p>
            )}
          </div>

          {/* Focus Keywords */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2 tracking-tight">
              Focus Keywords {formData.blogType === 'Thought & Leadership' ? <span className="text-gray-400 font-normal">(Optional)</span> : <span className="text-sky-600">*</span>}
            </label>
            <input
              type="text"
              name="focusKeywords"
              value={formData.focusKeywords}
              onChange={(e) => {
                handleInputChange(e);
                // Recalculate density immediately for responsive UX
                calculateKeywordDensity();
              }}
              className="w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none bg-white hover:border-sky-300"
              placeholder="Primary keywords separated by commas"
            />

            {/* Keyword Density Analysis */}
            {keywordDensity.length > 0 && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-800 flex items-center tracking-tight">
                    <span className="mr-2">📊</span>
                    Keyword Density Analysis
                  </h4>
                  <button
                    type="button"
                    onClick={() => {
                      calculateKeywordDensity();
                    }}
                    className="text-xs px-3 py-1.5 bg-sky-100 text-sky-700 rounded-lg hover:bg-sky-200 transition-colors font-semibold border border-sky-200"
                  >
                    Debug Count
                  </button>
                </div>

                <div className="space-y-2">
                  {keywordDensity.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gradient-to-r from-sky-50/50 to-blue-50/50 rounded-lg border border-sky-200">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-semibold text-gray-900">&ldquo;{item.keyword}&rdquo;</span>
                        <span className="text-xs text-gray-600 font-medium">({item.count} occurrences)</span>
                      </div>

                      <div className="flex items-center space-x-2">
                        <span className={`text-sm font-semibold ${item.status === 'optimal' ? 'text-green-600' :
                          item.status === 'too-low' ? 'text-amber-600' : 'text-red-600'
                          }`}>
                          {item.density}%
                        </span>

                        <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${item.status === 'optimal' ? 'bg-green-100 text-green-800 border-green-200' :
                          item.status === 'too-low' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                            'bg-red-100 text-red-800 border-red-200'
                          }`}>
                          {item.status === 'optimal' ? 'Optimal' :
                            item.status === 'too-low' ? 'Too Low' : 'Too High'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Content Stats */}
                <div className="mt-3 p-3 bg-gradient-to-r from-sky-50/50 to-blue-50/50 rounded-lg border border-sky-200">
                  <div className="text-sm text-gray-800 font-semibold">
                    Content Statistics:
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs font-medium text-gray-700">
                      <div>Total words: <span className="font-semibold text-sky-700">
                        {(() => {
                          if (editor) {
                            const plainText = editor.getText();
                            const words = plainText.split(/\s+/).filter(word =>
                              word.length > 0 &&
                              word.replace(/[^\w]/g, '').length > 0
                            );
                            return words.length;
                          }
                          return 0;
                        })()}
                      </span></div>
                      <div>Keywords analyzed: <span className="font-semibold text-sky-700">{keywordDensity.length}</span></div>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="mt-3 p-3 bg-gradient-to-r from-sky-50 to-blue-50 rounded-lg border border-sky-200">
                  <div className="text-sm text-sky-800 font-semibold">
                    SEO Tips:
                    <ul className="mt-2 ml-4 list-disc text-xs space-y-1 font-medium text-sky-700">
                      <li>Optimal keyword density is 1-3% for best SEO results</li>
                      <li>Too low density may hurt search rankings</li>
                      <li>Too high density may be seen as keyword stuffing</li>
                      <li>Keywords are highlighted in yellow in the content editor</li>
                      <li>Click &ldquo;Debug Count&rdquo; to see detailed counting information in console</li>
                    </ul>
                  </div>
                </div>


                {formData.focusKeywords.trim() && keywordDensity.length === 0 && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="text-sm text-amber-800 font-semibold">
                      <strong>Note:</strong> Add content to see keyword density analysis. Keywords will be highlighted as you type.
                    </div>
                  </div>
                )}

              </div>
            )}
          </div>

          {/* Canonical URL */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2 tracking-tight">
              Canonical URL <span className="text-sky-600">*</span>
              <span className="ml-2 text-xs text-gray-500 font-medium">(Auto-generated from blog slug)</span>
            </label>
            <div className="flex space-x-2">
              <input
                type="url"
                name="canonicalUrl"
                value={formData.canonicalUrl}
                onChange={handleInputChange}
                className="flex-1 border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none bg-white hover:border-sky-300"
                placeholder="Will be auto-generated from blog slug"
                required
              />
              <button
                type="button"
                onClick={generateCanonicalUrl}
                className="px-4 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg hover:from-sky-600 hover:to-blue-700 transition-all font-semibold text-sm shadow-sm hover:shadow-md whitespace-nowrap"
              >
                Generate
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-sky-200/50 my-6"></div>

          {/* Social Media */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 tracking-tight mb-4">Social Media</h3>

            <div className="space-y-4">
              {/* Open Graph */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center tracking-tight">
                  <Globe className="h-4 w-4 mr-1.5 text-sky-600" />
                  Open Graph <span className="text-sky-600">*</span>
                </h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="ogTitle"
                    value={formData.ogTitle}
                    onChange={handleInputChange}
                    className="w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none bg-white hover:border-sky-300"
                    placeholder="OG Title"
                  />
                  <textarea
                    name="ogDescription"
                    value={formData.ogDescription}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm font-medium transition-all focus:border-sky-500 focus:outline-none outline-none resize-none bg-white hover:border-sky-300"
                    placeholder="OG Description"
                  />
                  <div className="space-y-2">
                    <input
                      type="url"
                      name="ogImage"
                      value={formData.ogImage}
                      onChange={handleInputChange}
                      className="w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none bg-white hover:border-sky-300"
                      placeholder="OG Image URL or leave empty to use featured image"
                    />
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500 font-medium">or</span>
                      <label className="cursor-pointer">
                        <span className="text-sky-600 hover:text-sky-700 text-sm underline font-semibold transition-colors">Upload image</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleOgImageUpload}
                          className="hidden"
                        />
                      </label>
                    </div>
                    {formData.ogImage && (
                      <div className="relative inline-block">
                        <Image
                          src={formData.ogImage}
                          alt="OG Image Preview"
                          width={80}
                          height={80}
                          className="w-20 h-20 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const event = {
                              target: { name: 'ogImage', value: '' }
                            } as React.ChangeEvent<HTMLInputElement>;
                            handleInputChange(event);
                          }}
                          className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 text-xs"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Twitter Card */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center tracking-tight">
                  <Twitter className="h-4 w-4 mr-1.5 text-sky-600" />
                  Twitter Card <span className="text-sky-600">*</span>
                </h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="twitterTitle"
                    value={formData.twitterTitle}
                    onChange={handleInputChange}
                    className="w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none bg-white hover:border-sky-300"
                    placeholder="Twitter Title"
                  />
                  <textarea
                    name="twitterDescription"
                    value={formData.twitterDescription}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm font-medium transition-all focus:border-sky-500 focus:outline-none outline-none resize-none bg-white hover:border-sky-300"
                    placeholder="Twitter Description"
                  />
                  <div className="space-y-2">
                    <input
                      type="url"
                      name="twitterImage"
                      value={formData.twitterImage}
                      onChange={handleInputChange}
                      className="w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm font-normal transition-all focus:border-sky-500 focus:outline-none outline-none bg-white hover:border-sky-300"
                      placeholder="Twitter Image URL or leave empty to use featured image"
                    />
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500 font-medium">or</span>
                      <label className="cursor-pointer">
                        <span className="text-sky-600 hover:text-sky-700 text-sm underline font-semibold transition-colors">Upload image</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleTwitterImageUpload}
                          className="hidden"
                        />
                      </label>
                    </div>
                    {formData.twitterImage && (
                      <div className="relative inline-block">
                        <Image
                          src={formData.twitterImage}
                          alt="Twitter Image Preview"
                          width={80}
                          height={80}
                          className="object-cover rounded border"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const event = {
                              target: { name: 'twitterImage', value: '' }
                            } as React.ChangeEvent<HTMLInputElement>;
                            handleInputChange(event);
                          }}
                          className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 text-xs"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {afterSocialMediaBlock}
          </div>

          {/* Divider */}
          <div className="border-t border-sky-200/50 my-6"></div>

          {/* Schema Markup */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 tracking-tight mb-4">Schema Markup <span className="text-sky-600">*</span></h3>

            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Enable Schema Markup
                  </label>
                  <input
                    type="checkbox"
                    checked={formData.enableSchema}
                    onChange={(e) => {
                      const event = {
                        target: { name: 'enableSchema', value: e.target.checked, type: 'checkbox' }
                      } as unknown as React.ChangeEvent<HTMLInputElement>;
                      handleInputChange(event);
                    }}
                    className="h-4 w-4 text-primary focus:outline-none border-gray-300 rounded"
                  />
                </div>
                <button
                  type="button"
                  onClick={generateSchemaMarkup}
                  className="w-full px-4 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg hover:from-sky-600 hover:to-blue-700 transition-all font-semibold text-sm shadow-sm hover:shadow-md"
                >
                  Generate Schema
                </button>
                <textarea
                  name="schemaMarkup"
                  value={formData.schemaMarkup}
                  onChange={handleInputChange}
                  rows={8}
                  className="w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 text-xs font-mono font-normal transition-all focus:border-sky-500 focus:outline-none outline-none resize-none bg-white hover:border-sky-300"
                  placeholder="JSON-LD schema markup will appear here..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
